import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { HomeHero } from "@/components/home/HomeHero";
import { TopPicksSection } from "@/components/home/TopPicks";
import { CategorySection } from "@/components/home/CategorySection";
import { StickyTopPick } from "@/components/home/StickyTopPick";
import { AdSlot } from "@/components/AdSlot";

const score = (p) => (p.featured ? 1 : 0) + p.rating * Math.log10((p.review_count || 0) + 10);

function ListsStrip({ lists }) {
  if (!lists.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-2">Buying guides</p>
          <h2 className="font-display text-4xl text-[#2C2B29]">Not sure what to buy?</h2>
        </div>
        <Link to="/lists" data-testid="see-all-lists" className="hh-link text-sm font-medium">See all →</Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {lists.slice(0, 3).map((l) => (
          <Link key={l.id} to={`/list/${l.id}`} data-testid={`home-list-${l.id}`} className="hh-card rounded-2xl overflow-hidden group">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={l.hero_image} alt={l.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-6">
              <span className="text-xs uppercase tracking-wider text-[#5B7B53]">{l.category}</span>
              <h3 className="font-display text-2xl text-[#2C2B29] mt-2 leading-tight group-hover:text-[#5B7B53] transition-colors">{l.title}</h3>
              <p className="text-sm text-[#5C5A56] mt-2 line-clamp-2">{l.subtitle}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function HomePage() {
  const [products, setProducts] = useState([]);
  const [lists, setLists] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data));
    api.get("/lists", { params: { featured: true } }).then((r) => setLists(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const ranked = useMemo(() => [...products].sort((a, b) => score(b) - score(a)), [products]);
  const topPicks = ranked.slice(0, 8);
  const topPick = topPicks.length ? topPicks[new Date().getDate() % Math.min(3, topPicks.length)] : null;

  return (
    <div className="pb-20 md:pb-0">
      <SEO
        title=""
        description="HomeHearth — the most useful, best-reviewed home & kitchen products on Amazon.in. Top picks with key benefits, prices in ₹ and direct buy links."
        path="/"
      />
      <HomeHero topPick={topPick} categories={categories} productCount={products.length} />
      <TopPicksSection products={topPicks} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10"><AdSlot type="display" testid="home-ad-after-top-picks" /></div>
      {categories.map((c, i) => (
        <CategorySection key={c.id} index={i} category={c} products={ranked.filter((p) => p.category === c.name)} />
      ))}
      <ListsStrip lists={lists} />
      <StickyTopPick product={topPick} />
    </div>
  );
}
